"use client";

import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import clsx from "clsx";
import { Icon, ICON_TYPE, type IconNameExtended } from "@/components/library/Icon";
import { IClosedInlineWidget } from "@/components/common/IClosedInlineWidget";
import { WistiaVslEmbed } from "@/components/landing/WistiaVslEmbed";
import { MarketingPartnerTicker } from "@/components/redesign/MarketingPartnerTicker";
import { Input } from "@/components/ui/input";
import { BookDemoButton } from "@/puck/BookDemoButton";
import { useBlockAnchorId } from "@/puck/blockAnchors";
import { buildIClosedSchedulerUrl } from "@/utils/iclosed";
import type {
  BookDemoBlockProps,
  FaqBlockProps,
  HeroBlockProps,
  PartnerTickerBlockProps,
  WistiaVideoBlockProps,
} from "@/puck/types";

/** Sprite the builder draws icons from. `light` is the richest set bundled in
 *  `public/sprites/` and is also the `<Icon>` default type, so picker choices
 *  always resolve to a real glyph. */
export const BUILDER_ICON_TYPE = ICON_TYPE.LIGHT;
const ICON_SPRITE_URL = "/sprites/v3/light.svg";

/** Cached promise so the (potentially large) sprite is fetched/parsed once even
 *  when several icon fields mount at the same time. */
let iconNamesCache: Promise<string[]> | null = null;

function loadBuilderIconNames(): Promise<string[]> {
  if (!iconNamesCache) {
    iconNamesCache = fetch(ICON_SPRITE_URL)
      .then((res) => (res.ok ? res.text() : ""))
      .then((svg) => {
        const names = new Set<string>();
        const symbolPattern = /<symbol\b[^>]*\sid="([^"]+)"/g;
        let match: RegExpExecArray | null;
        while ((match = symbolPattern.exec(svg)) !== null) {
          names.add(match[1]);
        }
        return [...names].sort((a, b) => a.localeCompare(b));
      })
      .catch(() => []);
  }
  return iconNamesCache;
}

/**
 * Custom Puck field: a searchable grid of every icon available in the bundled
 * sprite, rendered with the real `<Icon>` so editors see the actual glyph.
 */
export function IconPickerField({
  value = "",
  onChange,
}: Readonly<{ value?: string; onChange: (value: string) => void }>) {
  const [names, setNames] = useState<string[]>([]);
  const [query, setQuery] = useState("");

  useEffect(() => {
    let active = true;
    loadBuilderIconNames().then((loaded) => {
      if (active) setNames(loaded);
    });
    return () => {
      active = false;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const source = q ? names.filter((name) => name.includes(q)) : names;
    return source.slice(0, 150);
  }, [names, query]);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-gail-primary-light text-gail-primary">
          {value ? (
            <Icon
              icon={value as IconNameExtended}
              type={BUILDER_ICON_TYPE}
              className="h-4 w-4"
            />
          ) : null}
        </span>
        <Input
          type="text"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={value ? `Selected: ${value}` : "Search icons…"}
          className="border-black/15 bg-white px-2.5 py-1.5 text-black focus:border-gail-primary"
        />
      </div>
      <div className="grid max-h-44 grid-cols-6 gap-1 overflow-y-auto rounded-md border border-black/10 bg-white p-1.5">
        {names.length === 0 ? (
          <p className="col-span-6 px-1 py-3 text-center text-xs text-black/40">
            Loading icons…
          </p>
        ) : filtered.length === 0 ? (
          <p className="col-span-6 px-1 py-3 text-center text-xs text-black/40">
            No icons match “{query}”.
          </p>
        ) : (
          filtered.map((name) => {
            const selected = name === value;
            return (
              <button
                key={name}
                type="button"
                title={name}
                onClick={() => onChange(name)}
                className={clsx(
                  "flex aspect-square items-center justify-center rounded-md transition-colors hover:bg-gail-primary-light hover:text-gail-primary",
                  selected
                    ? "bg-gail-primary text-white hover:bg-gail-primary hover:text-white"
                    : "text-black/70",
                )}
              >
                <Icon
                  icon={name as IconNameExtended}
                  type={BUILDER_ICON_TYPE}
                  className="h-4 w-4"
                />
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

/**
 * Editor-only hint shown in place of a Wistia embed when no media ID is set.
 * Rendered only while editing (`puck.isEditing`) so published pages never show
 * a placeholder or a broken/empty `<wistia-player media-id="">`.
 */
function VideoIdPlaceholder({ dark = false }: Readonly<{ dark?: boolean }>) {
  return (
    <div
      className={clsx(
        "flex aspect-video w-full items-center justify-center rounded-[20px] border border-dashed px-6 text-center",
        dark ? "border-white/30 bg-white/5" : "border-black/20 bg-black/3",
      )}
    >
      <p
        className={clsx(
          "font-monument text-sm",
          dark ? "text-white/70" : "text-black/45",
        )}
      >
        Add a Wistia media ID in this block&apos;s settings to show the video.
      </p>
    </div>
  );
}

export function BuilderPartnerTickerBlock({
  label,
  compact = true,
  showDivider = true,
}: Readonly<PartnerTickerBlockProps>) {
  return (
    <MarketingPartnerTicker
      ariaLabel={label || "Partner logos"}
      label={label}
      compact={compact}
      showDivider={showDivider}
    />
  );
}

export function BuilderHeroBlock({
  eyebrow,
  title,
  accent,
  subtitle,
  primaryCtaLabel = "",
  primaryCtaHref = "#book-demo",
  secondaryCtaLabel = "",
  secondaryCtaHref = "#",
  showVideo = false,
  videoMediaId = "",
  videoAutoplay = false,
  footnotes = [],
  isEditing = false,
}: Readonly<HeroBlockProps & { isEditing?: boolean }>) {
  const hasPrimaryCta = primaryCtaLabel.trim().length > 0;
  const hasSecondaryCta = secondaryCtaLabel.trim().length > 0;
  const hasVideoId = videoMediaId.trim().length > 0;
  const videoContent = hasVideoId ? (
    <WistiaVslEmbed
      mediaId={videoMediaId.trim()}
      autoplay={isEditing ? false : videoAutoplay}
    />
  ) : isEditing ? (
    <VideoIdPlaceholder />
  ) : null;
  const notes = footnotes ?? [];

  return (
    <section className="relative overflow-hidden bg-marketing-surface px-4 pt-16 pb-12 md:pt-20">
      <div className="mx-auto flex w-full max-w-5xl flex-col items-center text-center">
        {eyebrow ? (
          <p className="font-mono text-xs uppercase tracking-[0.24em] text-gail-primary">
            {eyebrow}
          </p>
        ) : null}
        <h1 className="mt-5 max-w-5xl font-monument text-5xl font-bold leading-none tracking-tight text-black md:text-7xl">
          {title} {accent ? <span className="text-gail-primary">{accent}</span> : null}
        </h1>
        {subtitle ? (
          <p className="mx-auto mt-6 max-w-2xl font-monument text-lg leading-relaxed text-black/60 md:text-xl">
            {subtitle}
          </p>
        ) : null}

        {hasPrimaryCta || hasSecondaryCta ? (
          <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row">
            {hasPrimaryCta ? (
              <BookDemoButton href={primaryCtaHref}>{primaryCtaLabel}</BookDemoButton>
            ) : null}
            {hasSecondaryCta ? (
              <a
                href={secondaryCtaHref}
                className="rounded-md border border-black/10 px-5 py-2.5 font-monument text-sm font-medium text-black transition-colors hover:bg-black/5"
              >
                {secondaryCtaLabel}
              </a>
            ) : null}
          </div>
        ) : null}

        {showVideo && videoContent ? (
          <div className="mt-10 w-full max-w-4xl">{videoContent}</div>
        ) : null}

        {notes.length > 0 ? (
          <p className="mt-8 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 font-monument text-sm text-gail-gray">
            {notes.map((note, index) => (
              <span
                key={`${note.text}-${index}`}
                className="inline-flex items-center gap-2"
              >
                {index > 0 ? (
                  <span aria-hidden="true" className="text-gail-gray">
                    ·
                  </span>
                ) : null}
                {note.text}
              </span>
            ))}
          </p>
        ) : null}
      </div>
    </section>
  );
}

export function BuilderFaqBlock({
  eyebrow,
  title,
  accent,
  items,
}: Readonly<FaqBlockProps>) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section className="w-full border-t border-black/10 bg-marketing-voice-card px-4 py-20 md:py-28">
      <div className="mx-auto w-full max-w-3xl text-center">
        {eyebrow ? (
          <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-gail-primary">
            {eyebrow}
          </p>
        ) : null}
        <h2 className="mt-5 font-monument text-4xl font-bold leading-[1.08] tracking-tight text-black md:text-6xl">
          {title} <span className="text-gail-primary">{accent}</span>
        </h2>
      </div>

      <div className="mx-auto mt-12 w-full max-w-3xl md:mt-16">
        {items.map((item, index) => {
          const isOpen = openIndex === index;
          return (
            <div key={`${item.question}-${index}`} className="border-t border-black/10">
              <button
                type="button"
                onClick={() => setOpenIndex(isOpen ? null : index)}
                aria-expanded={isOpen}
                className="flex w-full items-center justify-between gap-6 py-6 text-left"
              >
                <span className="font-monument text-base text-black md:text-lg">
                  {item.question}
                </span>
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-black/5 text-black/40">
                  <Icon
                    icon="plus"
                    type={ICON_TYPE.SOLID}
                    className={clsx(
                      "h-3 w-3 transition-transform duration-300 ease-out",
                      isOpen && "rotate-45",
                    )}
                  />
                </span>
              </button>

              <AnimatePresence initial={false}>
                {isOpen ? (
                  <motion.div
                    key="content"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                    className="overflow-hidden"
                  >
                    <p className="max-w-2xl pb-6 font-monument text-base leading-relaxed text-gail-gray">
                      {item.answer}
                    </p>
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </div>
          );
        })}
        <div className="border-t border-black/10" />
      </div>
    </section>
  );
}

export function BuilderBookDemoBlock({
  eyebrow,
  title,
  accent,
  subtitle,
  iclosedEventUrl,
  iclosedEventTitle,
  benefits,
}: Readonly<BookDemoBlockProps>) {
  const schedulerUrl = useMemo(
    () => buildIClosedSchedulerUrl({}, iclosedEventUrl),
    [iclosedEventUrl],
  );
  const bookDemoId = useBlockAnchorId("book-demo");

  return (
    <section id={bookDemoId} className="w-full bg-gail-primary px-4 py-20 md:py-28">
      <div className="mx-auto w-full max-w-5xl">
        <div className="mx-auto max-w-2xl text-center">
          {eyebrow ? (
            <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-white/90">
              {eyebrow}
            </p>
          ) : null}
          <h2 className="mt-5 font-monument text-4xl font-bold leading-[1.08] tracking-tight text-white md:text-5xl">
            {title} <span className="text-white/70">{accent}</span>
          </h2>
          {subtitle ? (
            <p className="mx-auto mt-5 max-w-xl font-monument text-base leading-relaxed text-white/90 md:text-lg">
              {subtitle}
            </p>
          ) : null}
        </div>

        <div className="mx-auto mt-10 w-full max-w-5xl scroll-mt-24 rounded-2xl border border-black/10 bg-white px-3 pb-5 pt-4 shadow-[0_30px_70px_-40px_rgba(0,0,0,0.45)] md:px-4">
          <IClosedInlineWidget
            key={schedulerUrl}
            url={schedulerUrl}
            title={iclosedEventTitle}
            fallbackLabel="Open scheduler in a new tab"
          />
        </div>

        {benefits.length > 0 ? (
          <ul className="mx-auto mt-8 flex max-w-3xl flex-col flex-wrap items-center justify-center gap-3 sm:flex-row sm:gap-x-8">
            {benefits.map((benefit, index) => (
              <li
                key={`benefit-${index}`}
                className="flex items-center gap-2.5 font-monument text-sm text-white/90"
              >
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white/15 text-white">
                  <span aria-hidden="true" className="text-xs">
                    +
                  </span>
                </span>
                {benefit.text}
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </section>
  );
}

export function BuilderWistiaVideoBlock({
  eyebrow,
  title,
  subtitle,
  mediaId,
  autoplay,
  align,
  background,
  isEditing = false,
}: Readonly<WistiaVideoBlockProps & { isEditing?: boolean }>) {
  const isCentered = align === "center";
  const isPrimary = background === "primary";
  const hasMediaId = (mediaId ?? "").trim().length > 0;
  const videoContent = hasMediaId ? (
    <WistiaVslEmbed
      mediaId={mediaId.trim()}
      autoplay={isEditing ? false : autoplay}
    />
  ) : isEditing ? (
    <VideoIdPlaceholder dark={isPrimary} />
  ) : null;

  return (
    <section
      className={clsx(
        "w-full px-4 py-16 md:py-24",
        background === "white" && "bg-white",
        background === "surface" && "bg-marketing-surface",
        isPrimary && "bg-gail-primary text-white",
      )}
    >
      <div className="mx-auto w-full max-w-6xl">
        <div className={clsx("max-w-3xl", isCentered && "mx-auto text-center")}>
          {eyebrow ? (
            <p
              className={clsx(
                "font-mono text-[11px] uppercase tracking-[0.22em]",
                isPrimary ? "text-white/60" : "text-gail-primary",
              )}
            >
              {eyebrow}
            </p>
          ) : null}
          {title ? (
            <h2
              className={clsx(
                "mt-4 font-monument text-3xl font-bold leading-tight md:text-5xl",
                isPrimary ? "text-white" : "text-black",
              )}
            >
              {title}
            </h2>
          ) : null}
          {subtitle ? (
            <p
              className={clsx(
                "mt-5 font-monument text-base leading-relaxed md:text-lg",
                isPrimary ? "text-white/70" : "text-black/55",
              )}
            >
              {subtitle}
            </p>
          ) : null}
        </div>

        {videoContent ? (
          <div className="mx-auto mt-10 max-w-5xl">{videoContent}</div>
        ) : null}
      </div>
    </section>
  );
}

export function BuilderTextBlock({
  className,
  children,
}: Readonly<{ className?: string; children: React.ReactNode }>) {
  return <div className={clsx("font-monument", className)}>{children}</div>;
}
